/**
 * Hallyu4U Integrated System
 * Complete integration between Shopify theme and backend
 * Version: 1.0.0
 */

// Get API base URL based on environment
function getApiBaseUrl() {
  if (window.HALLYU4U_CONFIG?.apiUrl) {
    return window.HALLYU4U_CONFIG.apiUrl;
  }
  
  // Fallback to environment-based detection
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8080';
  }
  return 'https://hallyu4u.api.hallyu-manager.com';
}

// Get WebSocket URL based on environment
function getWebSocketUrl() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'ws://localhost:8080';
  }
  return 'wss://hallyu4u.api.hallyu-manager.com';
}

class Hallyu4UIntegratedSystem {
  constructor() {
    this.config = {
      apiBaseUrl: window.HALLYU4U_CONFIG?.apiUrl || getApiBaseUrl(),
      websocketUrl: window.HALLYU4U_CONFIG?.wsUrl || getWebSocketUrl(),
      shopifyDomain: window.Shopify?.shop || 'hallyu4u.myshopify.com',
      language: 'en', // Default language
      customer: null,
      authToken: null
    };

    this.modules = {
      auth: null,
      dashboard: null,
      packages: null,
      tracking: null,
      shipping: null,
      notifications: null
    };

    this.socket = null;
    this.initialized = false;
  }

  /**
   * Initialize the entire system
   */
  async init() {
    try {
      // // API Store가 로드될 때까지 대기
      // await this.waitForAPIStore();

      // Check if running in Shopify environment
      this.detectEnvironment();

      // Initialize authentication
      await this.initAuth();

      // Load customer data
      await this.loadCustomerData();

      // Initialize modules
      await this.initModules();

      // Setup WebSocket connection
      await this.setupWebSocket();

      // Setup global event listeners
      this.setupEventListeners();

      this.initialized = true;

      // Trigger ready event
      this.dispatchEvent('system:ready', { customer: this.config.customer });

      return true;
    } catch (error) {
      console.error('??Failed to initialize Hallyu4U System:', error);
      this.handleInitError(error);
      return false;
    }
  }

  /**
   * Detect and configure environment
   */
  detectEnvironment() {
    // Check if we're in Shopify
    if (window.Shopify) {
      this.config.environment = 'shopify';
      this.config.shopifyDomain = window.Shopify.shop;

      // Get customer from Shopify
      if (window.__st && window.__st.cid) {
        this.config.customerId = window.__st.cid;
      }
    } else {
      this.config.environment = 'standalone';
    }

    // Check for API URL in meta tags or script attributes
    const apiMeta = document.querySelector('meta[name="hallyu4u-api-url"]');
    if (apiMeta) {
      this.config.apiBaseUrl = apiMeta.content;
    }

    // Get language preference
    this.config.language = this.getLanguagePreference();
  }

  /**
   * Initialize authentication system
   */
  async initAuth() {
    this.modules.auth = new Hallyu4UAuth(this.config);

    // Try to restore session from localStorage
    // 인증은 theme.liquid의 전역 인증 관리에서 처리하므로 여기서는 토큰만 복원
    const existingToken = this.getStoredToken();
    if (existingToken) {
      this.config.authToken = existingToken;
      // console.log('[Hallyu4U System] Restored token from storage');
    }
    
    // 인증 로직은 theme.liquid의 전역 인증 관리에서 처리됨
  }

  /**
   * CustomerDashboardAPI 인증 훅 대기
   */
  async waitForCustomerDashboardAPI(maxAttempts = 50) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const checkAPI = () => {
        attempts++;
        if (window.CustomerDashboardAPI?.Hooks?.useAuthenticateShopifyCustomer) {
          resolve(true);
        } else if (attempts < maxAttempts) {
          setTimeout(checkAPI, 100);
        } else {
          reject(new Error('CustomerDashboardAPI.Hooks not available after maximum attempts'));
        }
      };
      checkAPI();
    });
  }


  async authenticateShopifyCustomer() {
    try {
      await this.waitForCustomerDashboardAPI();

      const result = await window.CustomerDashboardAPI.Hooks.useAuthenticateShopifyCustomer(
        this.config.customerId
      );

      if (result.success) {
        this.config.authToken = result.token;
        this.storeToken(result.token);
      } else {
        console.error('Failed to authenticate Shopify customer:', result.error);
      }
    } catch (error) {
      console.error('Failed to authenticate Shopify customer:', error);
    }
  }

  /**
   * Load customer data from backend
   * 인증은 theme.liquid의 전역 인증 관리에서 처리되므로, 여기서는 토큰이 있을 때만 데이터 로드
   */
  async loadCustomerData() {
    // authToken이 없으면 스킵 (인증은 theme.liquid에서 처리됨)
    if (!this.config.authToken) {
      return;
    }

    try {
      // API Store의 auth.getMe 사용
      if (!window.Hallyu4UAPI || !window.Hallyu4UAPI.auth) {
        throw new Error('[Hallyu4U System] API Store not initialized');
      }

      const result = await window.Hallyu4UAPI.auth.getMe();
      if (result.success && result.data) {
        // API 응답 형식: { code, message, data: { id, email, ... } }
        this.config.customer = result.data.data || result.data;
      }
    } catch (error) {
      console.error('Failed to load customer data:', error);
    }
  }

  /**
   * Initialize all system modules
   */
  async initModules() {
    // if (document.querySelector('.h4u-dashboard, .h4u-dashboard-enhanced')) {
    //   this.modules.dashboard = new Hallyu4UDashboard(this.config, this);
    //   await this.modules.dashboard.init();
    // }

    // Package management module
    this.modules.packages = new Hallyu4UPackages(this.config, this);

    // Tracking module
    this.modules.tracking = new Hallyu4UTracking(this.config, this);

    // Shipping calculator module
    if (document.querySelector('.shipping-calculator')) {
      this.modules.shipping = new Hallyu4UShipping(this.config, this);
      await this.modules.shipping.init();
    }

    // Notification module
    this.modules.notifications = new Hallyu4UNotifications(this.config, this);
  }

  /**
   * Setup WebSocket connection for real-time updates
   */
  async setupWebSocket() {
    if (!this.config.authToken) {
      return;
    }

    try {
      // Use Socket.io if available
      if (typeof io !== 'undefined') {
        this.socket = io(this.config.websocketUrl, {
          auth: {
            token: this.config.authToken
          },
          transports: ['websocket', 'polling']
        });

        this.socket.on('connect', () => {

          // Subscribe to customer updates
          if (this.config.customer) {
            this.socket.emit('subscribe', {
              customerId: this.config.customer._id || this.config.customer.id,
              channels: ['packages', 'notifications', 'tracking']
            });
          }
        });

        this.socket.on('disconnect', () => {
          console.log('?�️ WebSocket disconnected');
        });

        // Handle real-time events
        this.setupSocketEventHandlers();
      }
    } catch (error) {
      console.error('Failed to setup WebSocket:', error);
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  setupSocketEventHandlers() {
    if (!this.socket) return;

    // Package updates
    this.socket.on('package:update', (data) => {
      this.modules.packages?.handleUpdate(data);
    });

    // New package arrival
    this.socket.on('package:new', (data) => {
      this.modules.notifications?.show(`New package arrived: ${data.trackingNumber}`, 'success');
    });

    // Tracking updates
    this.socket.on('tracking:update', (data) => {
      this.modules.tracking?.handleUpdate(data);
    });

    // System notifications
    this.socket.on('notification', (data) => {
      this.modules.notifications?.show(data.message, data.type);
    });

    // Points update
    this.socket.on('points:update', (data) => {
      if (this.config.customer) {
        this.config.customer.points = data.points;
        this.config.customer.tier = data.tier;
        this.modules.dashboard?.updatePointsDisplay();
      }
    });
  }

  /**
   * Setup global event listeners
   */
  setupEventListeners() {
    // Handle authentication events
    document.addEventListener('hallyu4u:login', (e) => {
      this.handleLogin(e.detail);
    });

    document.addEventListener('hallyu4u:logout', () => {
      this.handleLogout();
    });

    // Handle logout link clicks
    document.addEventListener('click', (e) => {
      const logoutLink = e.target.closest('.header-logout, .mobile-nav-logout');
      if (logoutLink) {
        e.preventDefault();
        e.stopPropagation();
        this.handleLogout();
      }
    });

    // Handle language changes
    document.addEventListener('hallyu4u:language-change', (e) => {
      this.changeLanguage(e.detail.language);
    });

    // Handle page navigation (for SPA-like behavior)
    if (window.history && window.history.pushState) {
      window.addEventListener('popstate', (e) => {
        this.handleNavigation(e);
      });
    }
  }

  /**
   * Make API call with authentication
   * API Store를 사용하여 통합 관리
   */
  async apiCall(endpoint, options = {}) {
    if (!window.Hallyu4UAPI) {
      throw new Error('[Hallyu4U System] API Store not initialized. Please ensure hallyu4u-api-store.js is loaded.');
    }

    const apiStore = window.Hallyu4UAPI;
    
    // 토큰이 있으면 옵션에 추가
    if (this.config.authToken) {
      options.token = this.config.authToken;
    }
    
    // Shopify 헤더 추가
    if (this.config.environment === 'shopify') {
      options.headers = {
        ...options.headers,
        'X-Shopify-Domain': this.config.shopifyDomain,
        'X-Shopify-Customer-Id': this.config.customerId
      };
    }
    
    // API Store의 request 사용
    const result = await apiStore.request(endpoint, {
      method: options.method || 'GET',
      body: options.body || options.data,
      headers: options.headers,
      token: options.token,
      key: options.key || endpoint
    });
    
    // 401 에러 처리
    if (!result.success && result.status === 401) {
      this.handleUnauthorized();
    }
    
    // 기존 형식과 호환: response.data 반환
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'API call failed');
    }
  }

  /**
   * Handle login
   */
  async handleLogin(credentials) {
    try {
      // API Store의 auth.login 사용
      if (!window.Hallyu4UAPI || !window.Hallyu4UAPI.auth) {
        throw new Error('[Hallyu4U System] API Store not initialized');
      }

      const result = await window.Hallyu4UAPI.auth.login(credentials);
      if (result.success) {
        // 토큰 저장
        if (result.data?.token) {
          this.config.authToken = result.data.token;
        }
        return result.data;
      }
      throw new Error(result.error || 'Login failed');

      if (response.success) {
        this.config.authToken = response.token;
        this.config.customer = response.customer;
        this.storeToken(response.token);

        // Reinitialize system with authenticated user
        await this.init();

        this.dispatchEvent('system:authenticated', { customer: this.config.customer });
      }

      return response;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  /**
   * Handle logout
   */
  async handleLogout() {
    try {
      // API Store의 auth.logout 사용
      if (!window.Hallyu4UAPI || !window.Hallyu4UAPI.auth) {
        throw new Error('[Hallyu4U System] API Store not initialized');
      }

      await window.Hallyu4UAPI.auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear local state
    this.config.authToken = null;
    this.config.customer = null;
    
    // 모든 스토리지 및 캐시 삭제
    this.clearAllStorage();
    await this.clearAllCache();

    // Disconnect WebSocket
    if (this.socket) {
      this.socket.disconnect();
    }

    // Reload or redirect
    if (this.config.environment === 'shopify') {
      // classic / new customer accounts 모두 대응 (snippets/h4u-logout-config.liquid)
      window.location.href = window.H4U_LOGOUT_URL || '/account/logout';
    } else {
      window.location.reload();
    }
  }

  /**
   * Handle unauthorized response
   */
  handleUnauthorized() {
    this.clearStoredToken();
    this.config.authToken = null;

    // Redirect to login
    if (this.config.environment === 'shopify') {
      window.location.href = '/account/login';
    } else {
      this.dispatchEvent('system:unauthorized');
    }
  }

  /**
   * Update unique code display
   */
  updateUniqueCodeDisplay() {
    const codeElement = document.getElementById('customer-unique-code');
    if (codeElement && this.config.customer?.uniqueCode) {
      codeElement.textContent = this.config.customer.uniqueCode;

      // Update Korean address code
      const addressCode = document.getElementById('korean-address-code');
      if (addressCode) {
        addressCode.textContent = this.config.customer.uniqueCode;
      }

      // Display QR code if element exists
      const qrContainer = document.getElementById('unique-code-qr');
      if (qrContainer && this.config.customer.qrCode) {
        qrContainer.innerHTML = `<img src="${this.config.customer.qrCode}" alt="QR Code" class="w-32 h-32">`;
      }
    }
  }

  /**
   * Change system language
   */
  async changeLanguage(language) {
    this.config.language = language;
    localStorage.setItem('h4u_language', language);

    // Update all modules
    Object.values(this.modules).forEach(module => {
      if (module && typeof module.changeLanguage === 'function') {
        module.changeLanguage(language);
      }
    });

    // Reload translations
    await this.loadTranslations(language);

    this.dispatchEvent('system:language-changed', { language });
  }

  /**
   * Load translations for the specified language
   */
  async loadTranslations(language) {
    try {
      const response = await axios.get(`/locales/${language}.json`);
      const translations = response.data;
      window.H4U_TRANSLATIONS = translations;
      return translations;
    } catch (error) {
      console.error(`Failed to load translations for ${language}:`, error);
      return {};
    }
  }

  /**
   * Get language preference
   */
  getLanguagePreference() {
    // Check localStorage
    const stored = localStorage.getItem('h4u_language');
    if (stored) return stored;

    // Check browser language
    const browserLang = navigator.language.substring(0, 2);
    const supportedLangs = ['en', 'ko', 'ja', 'zh', 'es'];

    if (supportedLangs.includes(browserLang)) {
      return browserLang;
    }

    return 'en'; // Default to English
  }

  /**
   * Translate key
   */
  t(key, replacements = {}) {
    const translations = window.H4U_TRANSLATIONS || {};
    let text = translations[key] || key;

    // Replace placeholders
    Object.keys(replacements).forEach(placeholder => {
      text = text.replace(`{{${placeholder}}}`, replacements[placeholder]);
    });

    return text;
  }

  /**
   * Token management
   */
  getStoredToken() {
    return localStorage.getItem('h4u_token') || sessionStorage.getItem('h4u_token');
  }

  storeToken(token) {
      localStorage.setItem('h4u_token', token);
    
  }

  clearStoredToken() {
    localStorage.removeItem('h4u_token');
    sessionStorage.removeItem('h4u_token');
  }

  /**
   * 모든 로컬스토리지 및 세션스토리지 삭제
   */
  clearAllStorage() {
    try {
      // 로컬스토리지 전체 삭제
      localStorage.clear();
      
      // 세션스토리지 전체 삭제
      sessionStorage.clear();
      
      console.log('All storage cleared');
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  /**
   * 모든 캐시 삭제
   */
  async clearAllCache() {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('All caches cleared');
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  /**
   * Dispatch custom event
   */
  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(`hallyu4u:${eventName}`, {
      detail,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(event);
  }

  /**
   * Handle initialization error
   */
  handleInitError(error) {
    // Show user-friendly error message
    const errorContainer = document.getElementById('h4u-error-container');
    if (errorContainer) {
      errorContainer.innerHTML = `
        <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong class="font-bold">System Error</strong>
          <span class="block sm:inline">Unable to initialize Hallyu4U system. Please refresh the page or contact support.</span>
        </div>
      `;
    }

    // Log detailed error for debugging
    console.error('Initialization error details:', {
      error,
      config: this.config,
      environment: this.config.environment
    });
  }

  /**
   * Handle navigation (SPA-like)
   */
  handleNavigation(event) {
    const path = window.location.pathname;
    console.log('Navigating to:', path);

    // Update active state in dashboard
    if (this.modules.dashboard) {
      this.modules.dashboard.handleNavigation(path);
    }
  }

  /**
   * Public API methods
   */

  // Get current customer
  getCustomer() {
    return this.config.customer;
  }

  // Get auth status
  isAuthenticated() {
    return !!this.config.authToken;
  }

  // Get current language
  getLanguage() {
    return this.config.language;
  }

  // Get system config
  getConfig() {
    return { ...this.config };
  }

  // Check if system is ready
  isReady() {
    return this.initialized;
  }
}

/**
 * Dashboard Module
 */
class Hallyu4UDashboard {
  constructor(config, system) {
    this.config = config;
    this.system = system;
    this.packages = [];
    this.selectedPackages = new Set();
    this.currentTab = 'warehouse';
  }

  async init() {
    console.log('Initializing dashboard module...');

    // Load dashboard data
    // await this.loadDashboardData();

    // Setup dashboard UI
    this.setupUI();

    // Setup event listeners
    this.setupEventListeners();
  }

  // async loadDashboardData() {
  //   try {
  //     // Load statistics
  //     const stats = await this.system.apiCall(`/customers/${this.config.customer._id}/statistics`);
  //     this.updateStatistics(stats.data);

  //     // Load packages
  //     const packages = await this.system.apiCall(`/packages?customerId=${this.config.customer._id}`);
  //     this.packages = packages.data || [];
  //     this.renderPackages();

  //   } catch (error) {
  //     console.error('Failed to load dashboard data:', error);
  //   }
  // }

  setupUI() {
    // Initialize tooltips, modals, etc.
    this.initializeTooltips();
    this.initializeModals();
  }

  setupEventListeners() {
    // Tab switching
    document.querySelectorAll('.tab-btn').forEach(btn => {
      btn.addEventListener('click', (e) => this.switchTab(e.target.dataset.tab));
    });

    // Package selection
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('package-checkbox')) {
        this.togglePackageSelection(e.target.dataset.packageId, e.target.checked);
      }
    });
  }

  // updateStatistics(stats) {
  //   // Update UI elements
  //   document.getElementById('total-packages').textContent = stats.totalPackages || 0;
  //   document.getElementById('in-warehouse').textContent = stats.inWarehouse || 0;
  //   document.getElementById('pending-ship').textContent = stats.pendingShip || 0;
  //   document.getElementById('shipped').textContent = stats.shipped || 0;
  //   document.getElementById('total-saved').textContent = `$${(stats.totalSaved || 0).toLocaleString()}`;
  // }


  switchTab(tabName) {
    this.currentTab = tabName;
    // Update UI
    document.querySelectorAll('.tab-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tabName);
    });

    document.querySelectorAll('.tab-content').forEach(content => {
      content.classList.toggle('hidden', !content.id.startsWith(tabName));
    });
  }

  togglePackageSelection(packageId, selected) {
    if (selected) {
      this.selectedPackages.add(packageId);
    } else {
      this.selectedPackages.delete(packageId);
    }

    // Note: Action buttons (consolidate-btn, ship-request-btn) are not used in current implementation
  }

  async requestConsolidation() {
    // Implementation for consolidation request
    console.log('Requesting consolidation for:', Array.from(this.selectedPackages));
  }

  async requestShipping() {
    // Implementation for shipping request
    console.log('Requesting shipping for:', Array.from(this.selectedPackages));
  }


  initializeTooltips() {
    // Initialize tooltips if library is available
  }

  initializeModals() {
    // Initialize modals if needed
  }

  handleNavigation(path) {
    // Handle navigation within dashboard
  }

  changeLanguage(language) {
    // Update UI text based on language
  }

  updatePointsDisplay() {
    // Update points display in UI
  }
}

/**
 * Packages Module
 */
class Hallyu4UPackages {
  constructor(config, system) {
    this.config = config;
    this.system = system;
  }

  handleUpdate(data) {
    console.log('Package update received:', data);
    // Handle package updates
  }
}

/**
 * Tracking Module
 */
class Hallyu4UTracking {
  constructor(config, system) {
    this.config = config;
    this.system = system;
  }

  handleUpdate(data) {
    console.log('Tracking update received:', data);
    // Handle tracking updates
  }
}

/**
 * Shipping Module
 */
class Hallyu4UShipping {
  constructor(config, system) {
    this.config = config;
    this.system = system;
  }

  async init() {
    console.log('Initializing shipping module...');
    // Initialize shipping calculator
  }
}

/**
 * Notifications Module
 */
class Hallyu4UNotifications {
  constructor(config, system) {
    this.config = config;
    this.system = system;
  }

  show(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white z-50 transform transition-all duration-300 translate-y-full`;

    const bgColors = {
      'success': 'bg-green-500',
      'error': 'bg-red-500',
      'warning': 'bg-yellow-500',
      'info': 'bg-blue-500'
    };

    notification.classList.add(bgColors[type] || bgColors.info);
    notification.textContent = message;

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.classList.remove('translate-y-full');
    }, 10);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.add('translate-y-full');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
}

/**
 * Authentication Module
 */
class Hallyu4UAuth {
  constructor(config) {
    this.config = config;
  }

  async validateToken(token) {
    try {
      const result = await window.Hallyu4UAPI.request('/auth/me', {
        method: 'GET',
        token: token,
        key: 'auth.validateToken'
      });

      return result.success && result.status === 200;
    } catch (error) {
      console.error('Token validation error:', error);
      // 재발급해보기
      
      return false;
    }
  }
}

/**
 * Initialize on DOM ready
 */
if (typeof window !== 'undefined') {
  // Create global instance
  window.Hallyu4U = new Hallyu4UIntegratedSystem();

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.Hallyu4U.init();
    });
  } else {
    // DOM is already loaded
    window.Hallyu4U.init();
  }

  // Export for use in other scripts
  window.Hallyu4UIntegratedSystem = Hallyu4UIntegratedSystem;
}
